import * as React from 'react';
import { inject, observer } from 'mobx-react';
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Popover,
  Table,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RangeSourceCard } from '../components/common/range-source-card';
import {
  BloodworkRange,
  CUSTOM_MARKER_SECTION_OPTIONS,
  CatalogMarker,
  CatalogRow,
  CatalogSection,
  RANGE_BOUNDS,
  RANGE_BOUND_LABELS,
  RANGE_OVERRIDE_LIMIT,
  RangeBound,
  RangeCatalog,
  RangeDraft,
  RangeEditEvaluation,
  RangeOverride,
  RangeSource,
  Section,
  buildRangeCatalog,
  displayPrecisionFor,
  evaluateRangeEdit,
  isAdultScope,
  knockOnSentence,
  rangeOverridesByScopeKey,
  rangeOverridesSignature,
  releasedEditableRangeAttributes,
  practitionerSource,
  rangeSourceFor,
  rangeSourceGroupsFor,
  scopeKeyForRow,
} from '../models';
import { resolveUnitSystem } from '../models/units/resolveUnitSystem';
import { UnitSystem } from '../models/units/unitConversions';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { myRangesVisible } from '../utils/myRangesVisibility';
// The section bands and marker lines are My Markers' own classes, so the two
// pages are one design and cannot drift apart.
import '../components/bloodworkz/custom-markers-table.css';
import './my-ranges.css';

interface MyRangesContainerProps {
  bloodworkEntryStore?: BloodworkEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

/** What the open dialog is about to do. */
type PendingAction =
  | { kind: 'save'; marker: CatalogMarker }
  | { kind: 'reset'; marker: CatalogMarker; row: CatalogRow }
  | { kind: 'resetAll' };

interface MyRangesContainerState {
  /** Typed into the search box; matches a marker's name or its attribute. */
  filter: string;
  /** Markers whose population table is open, by attribute. */
  expandedMarkers: string[];
  /** Sections folded shut. UI only, and per visit. */
  collapsedSections: string[];
  /** Whether the range request has come back, so an empty table can say why. */
  rangesLoaded: boolean;
  /** The outer card folded shut, as My Markers' can be. UI only, per visit. */
  cardCollapsed: boolean;
  /**
   * What the practitioner has typed, by scope key and bound, exactly as typed.
   * Unsaved: nothing here grades a report until the save dialog is confirmed.
   */
  drafts: Record<string, RangeDraft>;
  /** The dialog, and what confirming it will do. */
  pending?: PendingAction;
  /** A save or reset in flight, so the dialog's button cannot fire twice. */
  saving: boolean;
  /** Confirmation after a save, in the card body where the edit happened. */
  savedMessage?: string;
  /** A failed save, said where the practitioner is looking rather than lost. */
  saveError?: string;
  /**
   * Markers whose Sources panel is open, by attribute. UI only,
   * and per visit, like the section folds above.
   */
  expandedSources: string[];
  /**
   * The practitioner's own reason per population, as typed in the save
   * dialog. Seeded from the stored override so reopening the dialog shows
   * what they wrote last time rather than an empty box.
   */
  notes: Record<string, string>;
  /**
   * The population whose source line is open for editing in its popover, and
   * what is typed into it. Separate from `notes`, which is the save dialog's
   * copy: these two doors write the same field but are never open at once.
   */
  editingSource?: string;
  editingSourceDraft: string;
}

/* 28px, down from 31. With fifteen ferritin rows the field height is what
   sets the table's height, and 28 still clears the 17px source chip beside it
   and stays comfortable to type in. */
const INPUT_STYLE: React.CSSProperties = { fontSize: '13px', height: '28px' };

/**
 * The heading the report prints for a section, which is also what My Markers
 * and Report Settings call it. The enum value ("Iron", "WBC") is an internal
 * name the practitioner has never been shown.
 */
function sectionLabel(section: Section): string {
  const option = CUSTOM_MARKER_SECTION_OPTIONS.find(o => o.value === section);
  return option ? option.label : section;
}

/** "40 – 75", the way the dialog names a band. */
function band(min?: number, max?: number): string {
  return `${min == null ? '—' : min} – ${max == null ? '—' : max}`;
}

/**
 * The info icon in the card header: what this page is, read once.
 *
 * Same shape as My Markers' (`CustomMarkerLibraryHeader`) down to the class
 * and the click-stop, because the two pages are one design — and because the
 * icon sits on a header that FOLDS, so a click that reached the header would
 * close the page the popover is explaining.
 *
 * It carries what the intro paragraph deliberately does not: the three states
 * a number can be in, what a report shows and does not show, and the way back.
 * All of it is read once and never again, which is what a popover is for and
 * what a fourth paragraph on the page is not.
 */
/**
 * Allowlisted markers that have ONE row per sex covering every age, so an edit
 * to that row grades a child too. Ferritin is not one: it has six pediatric
 * bands. See RangeCatalog.isAdultScope() and my-ranges-spec.md §5.1.
 */
const MARKERS_WITHOUT_PEDIATRIC_ROWS: ReadonlySet<string> = new Set(['vitaminD']);

/**
 * Whether the markers on the page split their rows differently, which is what
 * decides between the two wordings of the population sentences.
 *
 * With ferritin alone (prod, while vitamin D is held) every marker splits on
 * sex, age, menopause and trimester, so the page keeps the wording prod has
 * shipped since 2026-10-12, word for word, at the user's request 2026-10-26:
 * "for some markers" reads as a hedge about nothing when there is one marker.
 * Once a marker without pediatric rows is on the page the hedge is load-bearing
 * and the newer wording takes over.
 */
function splitsDifferByMarker(): boolean {
  return releasedEditableRangeAttributes().some(attribute =>
    MARKERS_WITHOUT_PEDIATRIC_ROWS.has(attribute),
  );
}

/** How each allowlisted marker reads mid-sentence in the intro paragraph. */
const MARKER_PROSE_NAMES: Readonly<Record<string, string>> = {
  ferritin: 'Ferritin',
  vitaminD: 'vitamin D',
};

/**
 * "Ferritin is the first," or "Ferritin and vitamin D are the first two,".
 *
 * Derived rather than written out, because the sentence has to stay true in an
 * environment where the allowlist is gated. It names markers in allowlist
 * order, which is release order, so "the first" keeps meaning what it says.
 */
function releasedMarkerSentence(): string {
  const names = releasedEditableRangeAttributes().map(
    attribute => MARKER_PROSE_NAMES[attribute] || attribute,
  );
  if (names.length === 0) {
    return 'Markers are added as their ranges are sourced, and';
  }
  if (names.length === 1) {
    return `${names[0]} is the first, and`;
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]} are the first two, and`;
  }
  // Serial comma, per the house style.
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(', ')}, and ${last} are the first ${names.length}, and`;
}

export const MyRangesHelp: React.FC = () => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom-start"
    overlay={
      <Popover id="my-ranges-help" className="custom-marker-help-popover">
        <Popover.Title as="h6">How My Ranges works</Popover.Title>
        <Popover.Content>
          <p>
            The reference ranges LabSmarts grades a result against, replaced
            with <strong>your own</strong> on the markers listed here. Different
            clients are graded on different rows,{' '}
            {splitsDifferByMarker()
              ? 'by sex and, for some markers, age, menopause status, and trimester'
              : 'by sex, age, menopause status, and trimester'}
            , so each population is set separately.
          </p>
          <p>
            A number shown <em>in gray italics</em> is{' '}
            <strong>calculated</strong> from the lab range rather than entered,
            at 1 standard deviation inside each end for optimal and 1 outside
            for alarm. Type in one to set it yourself. Because they are
            calculated, changing the lab range <strong>moves them too</strong>,
            and the save dialog names each one before it happens.
          </p>
          <p>
            The six numbers must stay in order, low to high. An edit that would
            collapse a band is refused rather than warned about: the bar graphs
            divide by those gaps.
          </p>
          <p>
            A saved range applies to <strong>every</strong> report, including
            tests already analyzed. That is deliberate — a range you correct
            should reach the clients you have already seen, not only the next
            one — but it does mean a PDF already sent can disagree with the same
            report reopened today.
          </p>
          <p>
            Your reports mark the markers you have changed with a{' '}
            <span className="my-ranges-dot-legend">&bull;</span> beside the
            name, and hovering it shows both ranges and the date you changed it.{' '}
            <strong>A client&rsquo;s report never says a range was changed</strong>{' '}
            — the bands are yours, and nothing in their document points it out.
          </p>
          <p>
            <strong>Reset back to LabSmarts&rsquo; ranges</strong> sits on every
            row, whether or not you have changed it, and there is one for every
            marker at once at the top of the page.
          </p>
          {/* Two wordings; see splitsDifferByMarker(). Ferritin has six
              pediatric bands, so with ferritin alone the "no separate rows for
              children" warning would describe a case the page does not
              contain. */}
          {splitsDifferByMarker() ? (
            <p>
              Where a marker has its own rows for children or for each trimester
              of pregnancy, those are here too and you can set them. A pregnancy
              row says so again when you save it. Some markers have{' '}
              <strong>no separate rows for children</strong>, with a single row
              per sex covering every age, so changing that row changes how a
              child is graded as well. The save dialog names which population a
              row reaches before you confirm.
            </p>
          ) : (
            <p>
              Children and each trimester of pregnancy have their own rows, and
              you can set those too. A pregnancy row says so again when you save
              it.
            </p>
          )}
        </Popover.Content>
      </Popover>
    }
  >
    <span
      className="custom-marker-block-help"
      title="How My Ranges works"
      onClick={(e: any) => e.stopPropagation()}
    >
      <FontAwesomeIcon icon="info-circle" />
    </span>
  </OverlayTrigger>
);

/**
 * "My Ranges": the reference ranges a practitioner can set their own values
 * for, by client population, on its own page under the Test Results menu next
 * to My Markers.
 *
 * ONLY the markers on the editable allowlist are shown. The full table is
 * LabSmarts' own work, and a page that laid all 169 markers out in one place
 * would be handing it over; the markers a practitioner can argue with are the
 * ones they need to see. (The table still reaches the browser whole, because
 * every report is graded against it; this is a product surface, not a
 * security boundary.)
 *
 * What the practitioner types here is LIVE and RETROACTIVE: an override is
 * applied to the range table every time a report is opened, so saving one
 * rewrites every report they have ever produced, including tests already
 * analyzed and PDFs already sent. That is deliberate — the alternative is a
 * practitioner who corrects a range seeing no benefit until they re-open and
 * re-save every client — and the save dialog names the consequence before it
 * happens rather than a toast naming it afterwards.
 *
 * Built from the STORED rows, not the initialized set an analysis reads, so
 * the page can tell a number LabSmarts wrote down from one init() derived, and
 * so the practitioner's own numbers can go UNDERNEATH that arithmetic rather
 * than on top of it. The three are printed differently on purpose.
 */
@inject('bloodworkEntryStore', 'userProfileUIStore')
@observer
export class MyRangesContainer extends React.Component<
  MyRangesContainerProps,
  MyRangesContainerState
> {
  state: MyRangesContainerState = {
    filter: '',
    expandedMarkers: [],
    collapsedSections: [],
    rangesLoaded: false,
    cardCollapsed: false,
    drafts: {},
    saving: false,
    expandedSources: [],
    notes: {},
    editingSourceDraft: '',
  };

  /**
   * The catalog is rebuilt only when its inputs change. Building it resolves
   * all ~3,300 rows through init(), which is cheap once and wasteful on every
   * keystroke in the filter box — or in a range field.
   */
  private catalogCache?: {
    rows: unknown;
    count: number;
    unitSystem: UnitSystem;
    overrides: string;
    catalog: RangeCatalog;
    storedByScopeKey: Map<string, BloodworkRange>;
  };

  private unmounted = false;

  componentDidMount() {
    const { bloodworkEntryStore, userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
    // Shared with every report page, so this is the cached promise and not a
    // second download of the table. The stored rows are what this page reads;
    // the override-applied set is for the analyses.
    const load = bloodworkEntryStore?.loadStoredBloodworkRanges();
    if (load) {
      load.then(() => {
        if (!this.unmounted) this.setState({ rangesLoaded: true });
      });
    } else {
      this.setState({ rangesLoaded: true });
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  private unitSystem(): UnitSystem {
    return resolveUnitSystem({
      profile: this.props.userProfileUIStore?.currentUserProfile,
    });
  }

  /** Whether this practitioner may change anything on this page. */
  private canEdit(): boolean {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    return !!profile && profile.canUseCustomRanges();
  }

  /** The overrides in force, which an unentitled profile has none of. */
  private overrides(): RangeOverride[] {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    if (!profile || !profile.activeRangeOverrides) {
      return [];
    }
    return profile.activeRangeOverrides();
  }

  private build() {
    const { bloodworkEntryStore } = this.props;
    const rows = bloodworkEntryStore?.storedBloodworkRanges || [];
    const unitSystem = this.unitSystem();
    const overrides = this.overrides();
    const signature = rangeOverridesSignature(overrides);
    const cache = this.catalogCache;
    if (
      cache &&
      cache.rows === rows &&
      cache.count === rows.length &&
      cache.unitSystem === unitSystem &&
      cache.overrides === signature
    ) {
      return cache;
    }
    const full = buildRangeCatalog(
      rows,
      unitSystem,
      rangeOverridesByScopeKey(overrides),
    );
    // Only what the practitioner can edit: allowlisted markers, and within
    // them only the adult rows. Filtered HERE rather than in the catalog,
    // which stays whole for the override logic and the tests that pin every
    // row of the table; this page is the one place that shows less.
    const catalog: RangeCatalog = {
      skipped: full.skipped,
      sections: full.sections
        .map(section => ({
          section: section.section,
          markers: section.markers
            .filter(m => m.editable)
            .map(m => ({ ...m, rows: m.rows.filter(r => r.editable) }))
            .filter(m => m.rows.length > 0),
        }))
        .filter(section => section.markers.length > 0),
    };
    // The stored row behind each population, which is what an edit is
    // evaluated against: the override goes on a copy of it and init() runs
    // afterwards, the same order the analyses apply in.
    const storedByScopeKey = new Map<string, BloodworkRange>();
    rows.forEach(row => {
      if (row && row.attribute) {
        storedByScopeKey.set(scopeKeyForRow(row), row);
      }
    });
    this.catalogCache = {
      rows,
      count: rows.length,
      unitSystem,
      overrides: signature,
      catalog,
      storedByScopeKey,
    };
    return this.catalogCache;
  }

  private toggleMarker = (attribute: string) => {
    this.setState(state => ({
      expandedMarkers: state.expandedMarkers.includes(attribute)
        ? state.expandedMarkers.filter(a => a !== attribute)
        : [...state.expandedMarkers, attribute],
    }));
  };

  private toggleSection = (section: Section) => {
    this.setState(state => ({
      collapsedSections: state.collapsedSections.includes(section)
        ? state.collapsedSections.filter(s => s !== section)
        : [...state.collapsedSections, section],
    }));
  };

  private matches(marker: CatalogMarker, needle: string): boolean {
    if (!needle) return true;
    return (
      marker.label.toLowerCase().includes(needle) ||
      marker.attribute.toLowerCase().includes(needle)
    );
  }

  // ─── Editing ──────────────────────────────────────────────────────────────

  private onTyped = (scopeKey: string, bound: RangeBound, value: string) => {
    this.setState(state => ({
      drafts: {
        ...state.drafts,
        [scopeKey]: { ...(state.drafts[scopeKey] || {}), [bound]: value },
      },
      savedMessage: undefined,
      saveError: undefined,
    }));
  };

  /** Drop a marker's unsaved typing, leaving what is stored alone. */
  private discardDrafts = (marker: CatalogMarker) => {
    this.setState(state => {
      const drafts = { ...state.drafts };
      marker.rows.forEach(row => delete drafts[row.scopeKey]);
      return { drafts, saveError: undefined };
    });
  };

  /** The same, for one population. No dialog: nothing has been applied yet. */
  private discardRowDraft = (row: CatalogRow) => {
    this.setState(state => {
      const drafts = { ...state.drafts };
      delete drafts[row.scopeKey];
      return { drafts, saveError: undefined };
    });
  };

  private hasDrafts(marker: CatalogMarker): boolean {
    return marker.rows.some(row => !!this.state.drafts[row.scopeKey]);
  }

  /**
   * What each typed row would do if it were saved. Computed on every render of
   * an open marker with drafts, which is a handful of init() calls on a
   * handful of rows — the 3,300-row work is the catalog's, and that is cached.
   */
  private evaluate(
    marker: CatalogMarker,
  ): { row: CatalogRow; evaluation: RangeEditEvaluation }[] {
    const { storedByScopeKey } = this.build();
    const byScopeKey = rangeOverridesByScopeKey(this.overrides());
    const unitSystem = this.unitSystem();
    const precision = displayPrecisionFor(marker.attribute, unitSystem);
    return marker.rows
      .filter(
        // A row with no stored row behind it cannot happen — the catalog was
        // built from these same rows — but an edit evaluated against nothing
        // would resolve every bound to NaN, so it is skipped rather than
        // predicted wrongly.
        row => !!this.state.drafts[row.scopeKey] && !!storedByScopeKey.get(row.scopeKey),
      )
      .map(row => {
        const storedRow = storedByScopeKey.get(row.scopeKey);
        return {
          row,
          evaluation: evaluateRangeEdit({
            attribute: marker.attribute,
            storedRow,
            existing: byScopeKey.get(row.scopeKey) as RangeOverride,
            draft: this.state.drafts[row.scopeKey],
            scopeKey: row.scopeKey,
            unitSystem,
            precision,
          }),
        };
      });
  }

  private pendingEvaluations(): { row: CatalogRow; evaluation: RangeEditEvaluation }[] {
    const pending = this.state.pending;
    if (!pending || pending.kind !== 'save') {
      return [];
    }
    return this.evaluate(pending.marker).filter(
      entry => !entry.evaluation.unchanged,
    );
  }

  /** This population's stored override, if it has one. */
  private overrideFor(scopeKey: string): RangeOverride | undefined {
    return rangeOverridesByScopeKey(this.overrides()).get(scopeKey);
  }

  /**
   * What the practitioner has typed as their reason, falling back to what they
   * wrote last time rather than to an empty box. Reopening the dialog on a
   * population they have already annotated should show the annotation.
   */
  private noteFor(row: CatalogRow): string {
    const typed = this.state.notes[row.scopeKey];
    if (typed !== undefined) {
      return typed;
    }
    const existing = this.overrideFor(row.scopeKey);
    return (existing && existing.note) || '';
  }

  /**
   * "LabSmarts' Lab Min of 30 comes from ASH 2026 (PubMed 42747316)."
   *
   * One line per bound the practitioner is about to overrule, and only for
   * bounds that have an authored source. A bound LabSmarts derives says
   * nothing here: there is no paper to name, and a line saying so would be
   * noise in the one dialog that has to stay short.
   */
  private provenanceLines(
    row: CatalogRow,
    evaluation: RangeEditEvaluation,
  ): string[] {
    return evaluation.changedBounds.reduce((lines, bound) => {
      const source = rangeSourceFor(row.scopeKey, bound);
      if (!source || source.kind === 'derived') {
        return lines;
      }
      // evaluation.labSmarts, not the cell: CatalogRow only carries
      // labSmartsValue on a bound the practitioner has ALREADY overridden,
      // and this line is mostly read on the first override of a row.
      const value = evaluation.labSmarts[bound];
      const link = (source.links || [])[0];
      return [
        ...lines,
        `LabSmarts’ ${RANGE_BOUND_LABELS[bound]}${
          value == null ? '' : ` of ${value}`
        } comes from ${source.short}${link ? ` (${link.label})` : ''}.`,
      ];
    }, [] as string[]);
  }

  /** The list to store, with these scope keys replaced or removed. */
  private overridesWith(
    replacements: Map<string, RangeOverride | undefined>,
  ): RangeOverride[] {
    const kept = this.overrides().filter(
      override => !replacements.has(override.scopeKey),
    );
    const added: RangeOverride[] = [];
    replacements.forEach(override => {
      if (override) added.push(override);
    });
    return [...kept, ...added];
  }

  private confirm = () => {
    const pending = this.state.pending;
    if (!pending) {
      return;
    }
    const replacements = new Map<string, RangeOverride | undefined>();
    let touched: CatalogMarker | undefined;
    let next: RangeOverride[];

    if (pending.kind === 'save') {
      touched = pending.marker;
      this.evaluate(pending.marker).forEach(({ row, evaluation }) => {
        if (evaluation.errors.length > 0) {
          return;
        }
        // The practitioner's own reason rides on the override they are
        // confirming. noteFor() falls back to what is already stored, so a
        // later edit that does not retype it keeps it.
        const override = evaluation.override;
        if (override) {
          const note = this.noteFor(row).trim();
          if (note) {
            override.note = note;
          }
        }
        replacements.set(row.scopeKey, override);
      });
      next = this.overridesWith(replacements);
    } else if (pending.kind === 'reset') {
      touched = pending.marker;
      replacements.set(pending.row.scopeKey, undefined);
      next = this.overridesWith(replacements);
    } else {
      next = [];
    }

    if (next.length > RANGE_OVERRIDE_LIMIT) {
      this.setState({
        pending: undefined,
        saveError: `That would store more than ${RANGE_OVERRIDE_LIMIT} custom ranges, which is the limit. Reset some before adding more.`,
      });
      return;
    }

    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore) {
      return;
    }
    this.setState({ saving: true, saveError: undefined });
    userProfileUIStore.updateRangeOverrides(next).then(
      () => {
        if (this.unmounted) return;
        this.setState(state => {
          const drafts = { ...state.drafts };
          // Notes are cleared alongside drafts so the box reseeds from what
          // was actually stored. A reset removes the override and its note
          // with it, and a stale draft reappearing in the next dialog would
          // offer to save a reason for a change that no longer exists.
          const notes = { ...state.notes };
          if (pending.kind === 'reset') {
            delete notes[pending.row.scopeKey];
          } else if (touched) {
            touched.rows.forEach(row => delete notes[row.scopeKey]);
          } else {
            Object.keys(notes).forEach(key => delete notes[key]);
          }
          if (pending.kind === 'reset') {
            // One population went back. Typing on the marker's OTHER rows is
            // untouched work and survives — a practitioner who got the women's
            // row wrong does not lose the men's row they were halfway through.
            delete drafts[pending.row.scopeKey];
          } else if (touched) {
            touched.rows.forEach(row => delete drafts[row.scopeKey]);
          } else {
            // Reset all: every draft is about to be meaningless.
            Object.keys(drafts).forEach(key => delete drafts[key]);
          }
          return {
            drafts,
            notes,
            saving: false,
            pending: undefined,
            savedMessage:
              pending.kind === 'resetAll'
                ? 'Your ranges are back to LabSmarts’ for every marker.'
                : pending.kind === 'reset'
                ? `${touched!.label} is back to LabSmarts’ ranges for ${pending.row.label}.`
                : `${touched!.label} saved. Every report is now graded on your ranges.`,
          };
        });
      },
      (error: any) => {
        console.error(error);
        if (this.unmounted) return;
        this.setState({
          saving: false,
          pending: undefined,
          saveError:
            'Your ranges could not be saved, so nothing has changed. Try again in a moment.',
        });
      },
    );
  };

  // ─── Rendering ────────────────────────────────────────────────────────────

  /**
   * The chip beside a number that opens where it came from.
   *
   * A real `<button>` in a click-triggered OverlayTrigger, not a `title`
   * tooltip: a citation does not fit in a tooltip, a tooltip cannot be read at
   * leisure, and a tooltip cannot be clicked through to PubMed. It is also
   * where a lot of practitioners read this — on a tablet, where hover does not
   * exist at all.
   *
   * Renders nothing when the bound has no authored source, which is what lets
   * the allowlist widen one marker at a time: a marker with no entries in
   * RangeSources looks exactly as it did before this existed.
   */
  private renderSourceChip(row: CatalogRow, bound: RangeBound, shown?: number) {
    const source = rangeSourceFor(row.scopeKey, bound);
    if (!source) {
      return null;
    }
    const cell = row.bounds[bound];
    const label = RANGE_BOUND_LABELS[bound];
    return (
      <OverlayTrigger
        trigger="click"
        rootClose
        placement="auto"
        overlay={
          <Popover id={`src-${row.scopeKey}-${bound}`} className="my-ranges-source-popover">
            <Popover.Content>
              {/* Once the practitioner has overruled this number, LabSmarts'
                  citation is no longer the source of what is on screen.
                  Saying so is the difference between provenance and a lie by
                  placement. */}
              {cell.overridden ? (
                <div className="my-ranges-source-yours">
                  This is your number. LabSmarts&rsquo; is{' '}
                  <strong>{cell.labSmartsValue}</strong>, and its source is below.
                </div>
              ) : null}
              {/* The popover is a door, not only a window. Until this existed
                  the only way to record a source was to change a number, and
                  the only way to correct one was to change a number again —
                  wrong for a field whose whole purpose is keeping track. */}
              {cell.overridden && this.canEdit()
                ? this.renderSourceNote(row)
                : null}
              <RangeSourceCard
                source={source}
                heading={`${label}${shown == null ? '' : ` · ${shown}`}`}
              />
            </Popover.Content>
          </Popover>
        }
      >
        <button
          type="button"
          className={'my-ranges-source-chip' + this.chipState(row, cell)}
          aria-label={`Where ${label} for ${row.label} comes from`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          i
        </button>
      </OverlayTrigger>
    );
  }

  /**
   * The practitioner's own line about this population, shown in every one of
   * its six popovers and editable from any of them.
   *
   * Saving it does NOT open the retroactivity dialog, and that is the point
   * of the distinction: a note changes no number, grades no report and moves
   * no band. The dialog exists for edits that reach clients, and putting a
   * typo fix behind it would teach practitioners to dismiss it.
   */
  /**
   * Which of the three states this chip is in, as a class suffix.
   *
   *   (none)      LabSmarts' number, and the card is the published evidence
   *   mine-sourced  your number, and you recorded where it came from
   *   mine-bare     your number, nothing recorded — allowed, and not an error
   *
   * The third state is the entire nudge: visible while scanning a column,
   * silent otherwise. No red, no count, no banner.
   */
  private chipState(row: CatalogRow, cell: CatalogRow['bounds'][RangeBound]): string {
    if (!cell.overridden) {
      return '';
    }
    const stored = this.overrideFor(row.scopeKey);
    return stored && (stored.note || '').trim()
      ? ' my-ranges-chip-mine-sourced'
      : ' my-ranges-chip-mine-bare';
  }

  private renderSourceNote(row: CatalogRow) {
    const stored = this.overrideFor(row.scopeKey);
    const saved = (stored && stored.note) || '';
    const editing = this.state.editingSource === row.scopeKey;

    if (!editing) {
      return (
        <div className="my-ranges-source-mine">
          <p className="my-ranges-source-mine-label">Where this range comes from</p>
          {saved ? (
            <p className="my-ranges-source-mine-text">{saved}</p>
          ) : (
            <p className="my-ranges-source-mine-empty">
              Not recorded. For your own reference only.
            </p>
          )}
          <Button
            variant="link"
            className="my-ranges-source-mine-edit"
            onClick={() =>
              this.setState({
                editingSource: row.scopeKey,
                editingSourceDraft: saved,
              })
            }
          >
            {saved ? 'Edit' : 'Add a source'}
          </Button>
        </div>
      );
    }

    return (
      <div className="my-ranges-source-mine">
        <p className="my-ranges-source-mine-label">Where this range comes from</p>
        <Form.Control
          type="text"
          size="sm"
          autoFocus
          maxLength={300}
          placeholder="e.g. Hallberg 1993, bone-marrow confirmed"
          value={this.state.editingSourceDraft}
          aria-label={`Where your ${row.label} range comes from`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            // Read before setState: React 16 pools synthetic events.
            const typed = e.target.value;
            this.setState({ editingSourceDraft: typed });
          }}
        />
        <div className="my-ranges-source-mine-actions">
          <Button
            size="sm"
            variant="primary"
            disabled={this.state.saving}
            onClick={() => this.saveSourceNote(row)}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() =>
              this.setState({ editingSource: undefined, editingSourceDraft: '' })
            }
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  /** Write just the note onto the override this population already has. */
  private saveSourceNote = (row: CatalogRow) => {
    const existing = this.overrideFor(row.scopeKey);
    if (!existing) {
      this.setState({ editingSource: undefined, editingSourceDraft: '' });
      return;
    }
    const text = this.state.editingSourceDraft.trim();
    const next = new RangeOverride({ ...existing, note: text || undefined });
    // new RangeOverride() drops a blank note, so clearing the box clears the
    // field rather than storing an empty string.
    const replacements = new Map<string, RangeOverride | undefined>([
      [row.scopeKey, next],
    ]);
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore) {
      return;
    }
    this.setState({ saving: true, saveError: undefined });
    userProfileUIStore.updateRangeOverrides(this.overridesWith(replacements)).then(
      () => {
        if (this.unmounted) return;
        this.setState(state => ({
          saving: false,
          editingSource: undefined,
          editingSourceDraft: '',
          // The dialog's copy is reseeded from what was stored, so the two
          // doors cannot disagree about what is on file.
          notes: { ...state.notes, [row.scopeKey]: text },
        }));
      },
      (error: any) => {
        console.error(error);
        if (this.unmounted) return;
        this.setState({
          saving: false,
          editingSource: undefined,
          saveError:
            'Your source could not be saved, so nothing has changed. Try again in a moment.',
        });
      },
    );
  };

  private renderBoundCell(
    marker: CatalogMarker,
    row: CatalogRow,
    bound: RangeBound,
    precision: number,
  ) {
    const cell = row.bounds[bound];
    const draft = this.state.drafts[row.scopeKey];
    const typed = draft ? draft[bound] : undefined;
    const derived = cell.value != null && !cell.authored && !cell.overridden;
    const editable = row.editable && this.canEdit();
    const chip = this.renderSourceChip(row, bound, cell.value);

    if (!editable) {
      return (
        <td key={bound} className="my-ranges-bound">
          <span className="my-ranges-bound-cell">
            {/* The number has its own element rather than sitting straight in
                the cell, because the source chip shares the cell now and
                td.textContent would otherwise read "184.5i". */}
            <span
              className={
                'my-ranges-bound-value' +
                (derived ? ' my-ranges-derived' : '') +
                (cell.overridden ? ' my-ranges-mine' : '')
              }
              title={
                cell.overridden
                  ? `Your range. LabSmarts' is ${cell.labSmartsValue}`
                  : derived
                  ? 'Derived from the lab range'
                  : undefined
              }
            >
              {cell.value == null ? '' : cell.value}
            </span>
            {chip}
          </span>
        </td>
      );
    }

    const value = typed !== undefined ? typed : cell.value == null ? '' : String(cell.value);
    return (
      <td key={bound} className="my-ranges-bound">
        <span className="my-ranges-bound-cell">
          <Form.Control
            type="number"
            step={Math.pow(10, -precision)}
            className={
              'custom-marker-input my-ranges-input' +
              (derived && typed === undefined ? ' my-ranges-derived' : '') +
              (cell.overridden ? ' my-ranges-mine' : '') +
              (typed !== undefined ? ' my-ranges-typing' : '')
            }
            style={INPUT_STYLE}
            aria-label={`${marker.label}, ${row.label}, ${RANGE_BOUND_LABELS[bound]}`}
            title={
              cell.overridden
                ? `Your range. LabSmarts' is ${cell.labSmartsValue}`
                : derived
                ? 'Derived from the lab range'
                : undefined
            }
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              this.onTyped(row.scopeKey, bound, e.target.value)
            }
          />
          {chip}
        </span>
      </td>
    );
  }

  /**
   * The marker's citations: every bound, under its ranges.
   *
   * Called "Sources", the same as the control on the marker line, because
   * they are one thing with two doors and two names for it read as two
   * features. Either one opens and closes it; both set `expandedSources`.
   *
   * Grouped by BOUND rather than by population, which is both more compact
   * and more honest than repeating six rows three times: Lab Min collects
   * four citations across three populations while Alarm Min and Alarm Max
   * collect none and say so. Seeing that two of the six numbers are
   * LabSmarts' arithmetic is better than implying all six are cited.
   *
   * This is also the view a practitioner screenshots for a colleague, which
   * is why it is a panel rather than six separate hovers.
   */
  private renderSourcesPanel(marker: CatalogMarker, markerOpen: boolean) {
    const groups = rangeSourceGroupsFor(marker.rows);
    if (groups.length === 0) {
      return null;
    }
    const open = this.state.expandedSources.includes(marker.attribute);
    return (
      <>
        {/* Only when the ranges are showing. With them folded, the line's own
            Sources control sits a few pixels above this one and the two read
            as a doubled control. With fifteen population rows between, the
            line control is off the top of the screen and this is the only one
            in reach, which is the whole reason it exists. */}
        {markerOpen ? (
        <Button
          variant="link"
          className="my-ranges-why-toggle"
          aria-expanded={open}
          onClick={() =>
            this.setState(state => ({
              expandedSources: open
                ? state.expandedSources.filter(a => a !== marker.attribute)
                : [...state.expandedSources, marker.attribute],
            }))
          }
        >
          Sources{' '}
          <FontAwesomeIcon icon={open ? 'chevron-down' : 'chevron-right'} style={{ fontSize: '9px' }} />
        </Button>
        ) : null}
        {open ? (
          <div className="my-ranges-why">
            {groups.map(group => (
              <div key={group.bound} className="my-ranges-why-group">
                <div className="my-ranges-why-bound">
                  {RANGE_BOUND_LABELS[group.bound]}
                </div>
                <div className="my-ranges-why-entries">
                  {group.entries.map((entry, i) => (
                    <div key={i} className="my-ranges-why-entry">
                      <div className="my-ranges-why-applies">
                        {entry.appliesTo.length === marker.rows.length
                          ? 'all populations'
                          : entry.appliesTo.join(', ')}
                      </div>
                      <RangeSourceCard source={entry.source} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  /**
   * The way back, on every row, beside its Alarm Max.
   *
   * Present even where there is nothing to undo, which is the point: a
   * practitioner who has just made a mess of six numbers should SEE the way
   * out while they are looking at the mess, not have to work out that the
   * control appears only once a bad range has been saved. On a row that is
   * already LabSmarts' it is there and inert, and says so.
   *
   * It covers both kinds of mess, because they are not the same thing to undo:
   *
   *   typed, not saved  -> the typing is discarded on the spot. Nothing has
   *                        graded a report, so there is nothing to warn about
   *                        and nothing to save.
   *   saved             -> the dialog, then the save. Reverting is as
   *                        retroactive as the change was, so it gets the same
   *                        sentence about reports already sent.
   *
   * Per ROW rather than per marker, because the populations are graded
   * separately: a practitioner who got the women's row wrong should not have
   * to throw away the men's row they got right.
   */
  private renderRowReset(marker: CatalogMarker, row: CatalogRow) {
    if (!this.canEdit()) {
      return null;
    }
    const typed = !!this.state.drafts[row.scopeKey];
    const label = 'Reset back to LabSmarts’ ranges';
    if (!typed && !row.overridden) {
      return (
        <Button
          variant="link"
          className="my-ranges-reset"
          disabled
          title={`${row.label} already uses LabSmarts’ ranges.`}
        >
          {label}
        </Button>
      );
    }
    return (
      <Button
        variant="link"
        className="my-ranges-reset"
        title={
          row.overridden
            ? `Put LabSmarts’ ranges back for ${row.label}, on every report`
            : `Discard what you have typed for ${row.label}`
        }
        onClick={() =>
          row.overridden
            ? this.setState({
                pending: { kind: 'reset', marker, row },
                savedMessage: undefined,
              })
            : this.discardRowDraft(row)
        }
      >
        {label}
      </Button>
    );
  }

  private renderRows(marker: CatalogMarker) {
    const editable = this.canEdit();
    const precision = displayPrecisionFor(marker.attribute, this.unitSystem());
    const evaluations = this.evaluate(marker);
    const errors = evaluations.reduce(
      (all, entry) => [...all, ...entry.evaluation.errors],
      [] as string[],
    );
    const changed = evaluations.some(entry => !entry.evaluation.unchanged);
    return (
      <>
        <Table size="sm" className="my-ranges-table">
          <thead>
            <tr>
              <th>Population</th>
              {/* The label is centered over the INPUT, not over the cell. The
                  cell also holds the source chip, so centering on the cell
                  would sit every label half a chip's width right of the column
                  of numbers it names — and on a marker with no citations the
                  cells have no chip at all, so cell-centering would land
                  differently on different markers. A fixed box the width of
                  the input, left-anchored exactly as the input is, lands the
                  same way in every case. */}
              {RANGE_BOUNDS.map(bound => (
                <th key={bound} className="my-ranges-bound">
                  <span className="my-ranges-bound-label">
                    {RANGE_BOUND_LABELS[bound]}
                  </span>
                </th>
              ))}
              <th className="my-ranges-row-action" />
            </tr>
          </thead>
          <tbody>
            {marker.rows.map(row => (
              <tr key={row.scopeKey} data-scope-key={row.scopeKey}>
                <td className="my-ranges-population">{row.label}</td>
                {RANGE_BOUNDS.map(bound =>
                  this.renderBoundCell(marker, row, bound, precision),
                )}
                <td className="my-ranges-row-action">
                  {this.renderRowReset(marker, row)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {errors.length > 0 ? (
          <div className="my-ranges-errors" role="alert">
            {errors.map(error => (
              <div key={error}>{error}</div>
            ))}
          </div>
        ) : null}
        {editable && this.hasDrafts(marker) ? (
          <div className="my-ranges-edit-actions">
            {/* "Save", not "Save Ferritin ranges". The pair sits inside the
                marker's own open block, directly under its rows, so what it
                saves is said by where it is — and with two markers open the
                repeated name was the loudest thing on the page. The marker is
                named where the context is NOT in view: in the aria-label, for
                anyone tabbing through without seeing the table above, and in
                the dialog and the confirmation that follow. */}
            <Button
              size="sm"
              variant="primary"
              aria-label={`Save ${marker.label} ranges`}
              disabled={!changed || errors.length > 0}
              onClick={() =>
                this.setState({
                  pending: { kind: 'save', marker },
                  savedMessage: undefined,
                })
              }
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              aria-label={`Discard your unsaved ${marker.label} changes`}
              onClick={() => this.discardDrafts(marker)}
            >
              Cancel
            </Button>
            {/* Typed, but back to what is already in force — which is either
                LabSmarts' row or the practitioner's own saved one, so the
                sentence names neither. */}
            {!changed && errors.length === 0 ? (
              <span className="my-ranges-hint">
                Nothing has changed, so there is nothing to save.
              </span>
            ) : null}
          </div>
        ) : null}

      </>
    );
  }

  /**
   * "Sources", at the right edge of the marker line where My Markers keeps a
   * line's control.
   *
   * It used to leave the app for that marker's section of
   * labsmarts.com/bibliography. For a marker whose bounds are authored in
   * `RangeSources.ts` it no longer does: the same material is already here,
   * card for card, and sending a practitioner to a website to read what the
   * page below them already holds is the long way round. Clicking it opens
   * the marker and its sources panel, so the citations land directly under
   * the ranges they justify.
   *
   * A marker with no authored cards keeps the external link exactly as it
   * was, because for those four there is genuinely nothing in the app to
   * open. That is the honest split, and it disappears marker by marker as
   * Phase 1d authors them.
   *
   * The click is stopped either way, so it does not also fold the line.
   */
  private renderSourcesLink(marker: CatalogMarker) {
    const hasCards = rangeSourceGroupsFor(marker.rows).length > 0;

    if (!hasCards) {
      return marker.bibliographyUrl ? (
        <a
          className="my-ranges-line-sources"
          href={marker.bibliographyUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={`Where LabSmarts' ${marker.label} ranges come from (opens the bibliography)`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          Sources&nbsp;
          <FontAwesomeIcon icon="external-link-alt" style={{ fontSize: '10px' }} />
        </a>
      ) : null;
    }

    const open = this.state.expandedSources.includes(marker.attribute);
    return (
      <button
        type="button"
        className="my-ranges-line-sources my-ranges-line-sources-button"
        aria-expanded={open}
        title={`Where LabSmarts' ${marker.label} ranges come from, under the ranges`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          // Only the sources. It used to unfold the marker as well, back
          // when the panel lived inside the rows; now the panel stands on
          // its own, so this control is about citations and the chevron on
          // the line is about ranges.
          this.setState(state => ({
            expandedSources: open
              ? state.expandedSources.filter(a => a !== marker.attribute)
              : [...state.expandedSources, marker.attribute],
          }));
        }}
      >
        Sources{' '}
        <FontAwesomeIcon
          icon={open ? 'chevron-down' : 'chevron-right'}
          style={{ fontSize: '9px' }}
        />
      </button>
    );
  }

  private renderMarker(marker: CatalogMarker, forceOpen: boolean) {
    const open = forceOpen || this.state.expandedMarkers.includes(marker.attribute);
    const count = marker.rows.length;
    const mine = marker.rows.filter(row => row.overridden).length;
    return (
      <div key={marker.attribute} className="custom-marker-line">
        {/* The whole line opens the marker, as on My Markers; the name is a
            real button so the keyboard can reach it, and its click bubbles to
            the line rather than being wired a second time. */}
        <div
          className="custom-marker-line-head"
          onClick={() => this.toggleMarker(marker.attribute)}
        >
          <FontAwesomeIcon
            icon={open ? 'chevron-down' : 'chevron-right'}
            className="my-ranges-line-chevron"
          />
          <button
            type="button"
            className="custom-marker-line-name custom-marker-line-name-capped"
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} ${marker.label} ranges`}
          >
            {marker.label}
          </button>
          <span className="custom-marker-line-fact my-ranges-line-units">
            {marker.units || ''}
          </span>
          <span className="custom-marker-line-fact">
            {count === 1 ? '1 population' : `${count} populations`}
          </span>
          {/* Said on the folded line, because a practitioner scanning the page
              for what they have changed should not have to open five markers
              to find out. */}
          {mine > 0 ? (
            <span className="my-ranges-line-mine">
              {mine === count
                ? 'your ranges'
                : `your ranges on ${mine} of ${count}`}
            </span>
          ) : null}
          {this.renderSourcesLink(marker)}
        </div>
        {open ? this.renderRows(marker) : null}
        {/* OUTSIDE the rows. It used to render inside them, which meant
            clicking Sources on a folded marker unfolded fifteen population
            rows and put the citations below all of them, off the bottom of
            the screen: the practitioner clicked Sources and saw a table
            appear, not sources. Out here it lands directly under the line
            when the marker is folded, and under the ranges when it is open,
            which is where it belongs in both cases. */}
        {this.renderSourcesPanel(marker, open)}
      </div>
    );
  }

  private renderSection(section: CatalogSection, markers: CatalogMarker[], forceOpen: boolean) {
    const folded = this.state.collapsedSections.includes(section.section);
    return (
      <div key={section.section} className="custom-marker-group">
        <div className="custom-marker-group-heading">
          <div className="custom-marker-group-title">
            <Button
              variant="link"
              className="custom-marker-group-toggle"
              aria-expanded={!folded}
              onClick={() => this.toggleSection(section.section)}
            >
              <FontAwesomeIcon
                icon={folded ? 'chevron-right' : 'chevron-down'}
                style={{ fontSize: '11px' }}
              />
              &nbsp;&nbsp;{sectionLabel(section.section)}
            </Button>
            <span className="custom-marker-group-count">
              {markers.length === 1 ? '1 marker' : `${markers.length} markers`}
            </span>
          </div>
        </div>
        {folded
          ? null
          : markers.map(marker => this.renderMarker(marker, forceOpen))}
      </div>
    );
  }

  /**
   * The save dialog. Not a confirmation step for its own sake: it is the one
   * place the retroactivity is stated before it happens, and the one place a
   * derived bound that moved on its own is named. See my-ranges-spec.md §2.1.
   */
  private renderDialog() {
    const pending = this.state.pending;
    if (!pending) {
      return null;
    }
    const entries = this.pendingEvaluations();
    const warnings = entries.reduce(
      (all, entry) => [...all, ...entry.evaluation.warnings],
      [] as string[],
    );
    const title =
      pending.kind === 'save'
        ? `Save your ${pending.marker.label} ranges`
        : pending.kind === 'reset'
        ? `Reset ${pending.marker.label} to LabSmarts’ ranges`
        : 'Reset every marker to LabSmarts’ ranges';

    return (
      <Modal
        show
        onHide={() => this.setState({ pending: undefined })}
        centered
        className="my-ranges-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '17px' }}>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '13px' }}>
          {pending.kind === 'save'
            ? entries.map(({ row, evaluation }) => (
                <div key={row.scopeKey} className="my-ranges-dialog-row">
                  <div className="my-ranges-dialog-population">{row.label}</div>
                  {evaluation.changedBounds.map(bound => (
                    <div key={bound}>
                      {RANGE_BOUND_LABELS[bound]}{' '}
                      <strong>{evaluation.resolved[bound]}</strong>
                      {evaluation.labSmarts[bound] !== evaluation.resolved[bound]
                        ? `, replacing LabSmarts’ ${evaluation.labSmarts[bound]}`
                        : ''}
                    </div>
                  ))}
                  {/* stdDev is (labMax - labMin) / 4 and every bound LabSmarts
                      does not store is derived from it, so editing the lab
                      range moves numbers the practitioner never touched. */}
                  {evaluation.knockOns.length > 0 ? (
                    <div className="my-ranges-dialog-knockon">
                      Changing the lab range also moves{' '}
                      {evaluation.knockOns.map(knockOnSentence).join(', ')}.
                    </div>
                  ) : null}
                  {/* The only moment the practitioner is actively overruling
                      LabSmarts, and so the only moment naming what the number
                      rested on is worth anything. One line per bound, no link:
                      a link inside a modal is a trap, and the chip on the
                      table is one click away behind it. */}
                  {/* Children and pregnancy came off the lock on 2026-10-07
                      (§5.1). Both are populations a practitioner sees far less
                      of than adults, and a pregnancy range is the one where a
                      wrong number costs the most, so the dialog names which
                      one is being changed rather than leaving it to the row
                      label above. */}
                  {!isAdultScope(row.scope) ? (
                    <div className="my-ranges-dialog-scope">
                      {row.scope.trimester != null
                        ? 'This is a pregnancy row. Only clients recorded as pregnant in this trimester are graded on it.'
                        : 'This is a children’s row. Only clients in this age band are graded on it, and no adult report changes.'}
                    </div>
                  ) : null}
                  {this.provenanceLines(row, evaluation).map(line => (
                    <div key={line} className="my-ranges-dialog-provenance">
                      {line}
                    </div>
                  ))}
                  <Form.Group className="my-ranges-dialog-note">
                    <Form.Label htmlFor={`note-${row.scopeKey}`}>
                      Your source for this change (optional)
                    </Form.Label>
                    <Form.Control
                      id={`note-${row.scopeKey}`}
                      type="text"
                      size="sm"
                      maxLength={300}
                      placeholder="e.g. Hallberg 1993, bone-marrow confirmed"
                      value={this.noteFor(row)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // Read before setState: React 16 pools synthetic
                        // events, so e.target is null by the time a functional
                        // updater runs.
                        const typed = e.target.value;
                        this.setState(state => ({
                          notes: { ...state.notes, [row.scopeKey]: typed },
                        }));
                      }}
                    />
                  </Form.Group>
                </div>
              ))
            : null}
          {pending.kind === 'reset' ? (
            <div className="my-ranges-dialog-row">
              <div className="my-ranges-dialog-population">
                {pending.row.label}
              </div>
              <div>
                Back to LabSmarts&rsquo; lab{' '}
                {band(
                  pending.row.bounds.labMin.labSmartsValue,
                  pending.row.bounds.labMax.labSmartsValue,
                )}{' '}
                and optimal{' '}
                {band(
                  pending.row.bounds.optimalMin.labSmartsValue,
                  pending.row.bounds.optimalMax.labSmartsValue,
                )}
                .
              </div>
            </div>
          ) : null}
          {pending.kind === 'resetAll' ? (
            <p>
              Every custom range is removed and every marker goes back to
              LabSmarts&rsquo; ranges.
            </p>
          ) : null}
          {warnings.length > 0 ? (
            <Alert variant="warning" className="my-ranges-dialog-warning">
              {warnings.map(warning => (
                <div key={warning}>{warning}</div>
              ))}
            </Alert>
          ) : null}
          {/* The mitigation the design calls mandatory. Said before the save,
              naming what it reaches, not a toast afterwards. */}
          <p className="my-ranges-dialog-retroactive">
            This applies to <strong>every</strong> report, including tests
            already analyzed. Clients whose reports have already been sent may
            see different results if those reports are opened again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => this.setState({ pending: undefined })}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={this.state.saving}
            onClick={this.confirm}
          >
            {pending.kind === 'save' ? 'Save ranges' : 'Reset ranges'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    if (!myRangesVisible()) {
      return null;
    }

    const { catalog } = this.build();
    const needle = this.state.filter.trim().toLowerCase();
    const visible = catalog.sections
      .map(section => ({
        section,
        markers: section.markers.filter(m => this.matches(m, needle)),
      }))
      .filter(entry => entry.markers.length > 0);
    // Typing a name that narrows the page to one marker is a request to see
    // that marker's rows, so it opens without a second click.
    const totalMarkers = catalog.sections.reduce((n, s) => n + s.markers.length, 0);
    const totalShown = visible.reduce((n, e) => n + e.markers.length, 0);
    const forceOpen = !!needle && totalShown === 1;
    const editable = this.canEdit();
    const overrides = this.overrides();
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    const locked = !!profile && !profile.canUseCustomRanges();
    const stale = this.props.bloodworkEntryStore?.staleRangeOverrides || [];

    let empty: string | null = null;
    if (catalog.sections.length === 0) {
      empty = this.state.rangesLoaded
        ? 'The reference ranges could not be loaded. Reload the page to try again.'
        : 'Loading reference ranges…';
    } else if (visible.length === 0) {
      empty = `No marker matches "${this.state.filter.trim()}".`;
    }

    return (
      <Container fluid className="mt-4" style={{ paddingBottom: '40px' }}>
        <Card id="my-ranges-card">
          <Card.Header
            className="custom-marker-block-header"
            role="button"
            tabIndex={0}
            aria-expanded={!this.state.cardCollapsed}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.setState(state => ({ cardCollapsed: !state.cardCollapsed }));
              }
            }}
            onClick={() =>
              this.setState(state => ({ cardCollapsed: !state.cardCollapsed }))
            }
          >
            <FontAwesomeIcon
              icon={this.state.cardCollapsed ? 'chevron-right' : 'chevron-down'}
              style={{ fontSize: '11px' }}
            />
            &nbsp;&nbsp;My Ranges&nbsp;&nbsp;
            <MyRangesHelp />
          </Card.Header>
          {this.state.cardCollapsed ? null : (
            <Card.Body>
              {/* Names only the splits these rows actually have. The full
                  table also splits on ethnicity, elevation, menstrual phase
                  and MPV, but no row of an allowlisted marker does, and a
                  list of things the page never shows is noise. Age, menopause
                  and pregnancy are all named because every one of them is now
                  a row a practitioner can open and change.

                  "Every one of those rows is here, children and pregnancy
                  included" was removed 2026-10-12 (the user's call): the
                  popover's last paragraph already makes that promise at more
                  length, and naming trimester in the sentence above carries
                  the same point. This paragraph is the one place that must
                  not repeat the popover.

                  THE SPLITS ARE NO LONGER ONE MARKER'S. Ferritin splits on
                  all four; vitamin D splits on sex and trimester only and has
                  no age band at all, so "for some markers" came BACK on
                  2026-10-23 when vitamin D joined the allowlist. It was
                  removed on 2026-10-12 because it read as a hedge about
                  nothing when there was one marker, and it is load-bearing
                  again now that the two markers disagree.
                  EDITABLE_RANGE_ATTRIBUTES says so at the other end.

                  Naming ferritin as the FIRST marker is doing a job the page
                  cannot do otherwise. A practitioner who opens this and finds
                  one marker reads it as unfinished unless the page says the
                  list is meant to grow, and saying what decides the order is
                  what turns that into an invitation. */}
              <p style={{ fontSize: '13px', color: '#212529', marginBottom: '10px' }}>
                The markers you can set your own ranges for, with the ranges
                LabSmarts uses. {releasedMarkerSentence()} which markers come
                next depends on what practitioners ask for. A report uses the
                row that matches{' '}
                {splitsDifferByMarker()
                  ? 'the client, by sex and, for some markers, age, menopause status, and trimester in pregnancy'
                  : "the client's sex, age, menopause status, and trimester in pregnancy"}
                . A number shown{' '}
                <span className="my-ranges-legend">like this</span> is
                calculated from the lab range rather than entered directly, and
                one shown <span className="my-ranges-legend-mine">like this</span>{' '}
                is yours.
              </p>
              {/* The second sentence is a promise, and it belongs HERE, where
                  the practitioner decides whether to change a range — not in
                  release notes they will not re-read before sending a client
                  a PDF. What they need to be sure of at this moment is that
                  nothing in the client's document announces the change. */}
              {editable ? (
                <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                  A saved range applies to <strong>every</strong> report,
                  including tests already analyzed. Your reports mark the
                  markers you have changed with a{' '}
                  <span className="my-ranges-dot-legend">&bull;</span> on screen
                  so you can see which are yours; a client&rsquo;s report never
                  says a range was changed.
                </p>
              ) : null}
              {locked ? (
                <Alert variant="secondary" style={{ fontSize: '13px' }}>
                  Custom ranges are not on this account, so these are
                  LabSmarts&rsquo; ranges and every report is graded on them.
                  Ranges saved earlier are kept and will apply again if the
                  feature is turned back on.
                </Alert>
              ) : null}
              {/* A scope key that no longer matches a row: the population was
                  re-banded or removed under the override. Dropped rather than
                  applied to a row whose meaning moved, and said out loud,
                  because an override that silently stops working is the same
                  bug as one applied to the wrong row. */}
              {stale.length > 0 ? (
                <Alert variant="warning" style={{ fontSize: '13px' }}>
                  {stale.length === 1
                    ? 'One of your ranges no longer matches a population in the table, so it is not being applied: '
                    : `${stale.length} of your ranges no longer match a population in the table, so they are not being applied: `}
                  {stale.map(override => override.scopeKey).join(', ')}. Enter
                  it again on the row it belongs to.
                </Alert>
              ) : null}
              {this.state.savedMessage ? (
                <Alert variant="success" style={{ fontSize: '13px' }}>
                  {this.state.savedMessage}
                </Alert>
              ) : null}
              {this.state.saveError ? (
                <Alert variant="danger" style={{ fontSize: '13px' }} role="alert">
                  {this.state.saveError}
                </Alert>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px',
                }}
              >
                <Form.Control
                  type="text"
                  className="custom-marker-input"
                  style={{ ...INPUT_STYLE, maxWidth: '260px' }}
                  placeholder="Filter by marker"
                  aria-label="Find a marker"
                  value={this.state.filter}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    this.setState({ filter: e.target.value })
                  }
                />
                {needle && !empty ? (
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    Showing {totalShown} of {totalMarkers}
                  </span>
                ) : null}
                {editable && overrides.length > 0 ? (
                  <Button
                    variant="link"
                    className="my-ranges-reset"
                    style={{ marginLeft: 'auto' }}
                    onClick={() =>
                      this.setState({
                        pending: { kind: 'resetAll' },
                        savedMessage: undefined,
                      })
                    }
                  >
                    Reset all back to LabSmarts’ ranges
                  </Button>
                ) : null}
              </div>
              {empty ? (
                <div className="my-ranges-empty">{empty}</div>
              ) : (
                visible.map(entry =>
                  this.renderSection(entry.section, entry.markers, forceOpen),
                )
              )}
            </Card.Body>
          )}
        </Card>
        {this.renderDialog()}
      </Container>
    );
  }
}
